import type { ReactElement, ReactNode } from 'react';
import { t } from '@fetmode/i18n';
import { resolveAppUrl, useAuth } from '@fetmode/app-core';
import { Link } from 'react-router-dom';

import './studio-site-footer.css';

interface FooterLink {
  key: string;
  label: string;
  to?: string;
  href?: string;
}

interface FooterColumn {
  title: string;
  links: FooterLink[];
}

function consumerPath(path: string): string {
  return `${resolveAppUrl('consumer', import.meta.env).replace(/\/$/, '')}${path}`;
}

function buildColumns(loggedIn: boolean): FooterColumn[] {
  const studioLinks: FooterLink[] = loggedIn
    ? [
        { key: 'broadcast', label: t('common.studio.nav.broadcast'), to: '/live' },
        { key: 'settings', label: t('common.studio.nav.settings'), to: '/settings/overview' },
        {
          key: 'watchSite',
          label: t('common.studio.nav.watchSite'),
          href: resolveAppUrl('consumer', import.meta.env),
        },
      ]
    : [
        { key: 'broadcast', label: t('common.studio.nav.broadcast'), to: '/' },
        { key: 'signIn', label: t('common.studio.nav.signIn'), to: '/login' },
        {
          key: 'invite',
          label: t('common.nav.haveInvite'),
          href: consumerPath('/accept-invite'),
        },
        {
          key: 'watchSite',
          label: t('common.studio.nav.watchSite'),
          href: resolveAppUrl('consumer', import.meta.env),
        },
      ];

  return [
    {
      title: t('common.studio.footer.studioTitle'),
      links: studioLinks,
    },
    {
      title: t('common.footer.creatorsTitle'),
      links: [
        {
          key: 'agencies',
          label: t('common.footer.agencies'),
          href: resolveAppUrl('agency', import.meta.env),
        },
        {
          key: 'affiliates',
          label: t('common.footer.affiliates'),
          href: resolveAppUrl('partners', import.meta.env),
        },
        {
          key: 'creatorTools',
          label: t('common.footer.creatorTools'),
          href: resolveAppUrl('create', import.meta.env),
        },
      ],
    },
    {
      title: t('common.footer.supportTitle'),
      links: [
        { key: 'help', label: t('common.footer.help'), href: consumerPath('/p/help') },
        { key: 'safety', label: t('common.footer.safety'), href: consumerPath('/p/safety') },
        {
          key: 'contact',
          label: t('common.footer.contact'),
          href: 'mailto:support@fetmode.com',
        },
        {
          key: 'reportContent',
          label: t('common.footer.reportContent'),
          href: 'mailto:abuse@fetmode.com',
        },
      ],
    },
    {
      title: t('common.footer.companyTitle'),
      links: [
        { key: 'about', label: t('common.footer.about'), href: consumerPath('/p/about') },
        { key: 'careers', label: t('common.footer.careers'), href: consumerPath('/p/careers') },
        { key: 'press', label: t('common.footer.press'), href: consumerPath('/p/press') },
      ],
    },
    {
      title: t('common.footer.legalTitle'),
      links: [
        { key: 'terms', label: t('common.footer.terms'), href: consumerPath('/p/terms') },
        { key: 'privacy', label: t('common.footer.privacy'), href: consumerPath('/p/privacy') },
        { key: 'cookies', label: t('common.footer.cookies'), href: consumerPath('/p/cookies') },
        { key: 'usc2257', label: t('common.footer.usc2257'), href: consumerPath('/p/2257') },
        { key: 'dmca', label: t('common.footer.dmca'), href: consumerPath('/p/dmca') },
        {
          key: 'contentRemoval',
          label: t('common.footer.contentRemoval'),
          href: consumerPath('/p/content-removal'),
        },
        {
          key: 'guidelines',
          label: t('common.footer.guidelines'),
          href: consumerPath('/p/guidelines'),
        },
      ],
    },
  ];
}

function SocialIcon({ children }: { children: ReactNode }): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden focusable="false">
      {children}
    </svg>
  );
}

const SOCIAL = [
  {
    key: 'x',
    labelKey: 'common.footer.followX',
    href: 'https://x.com/fetmode',
    icon: (
      <SocialIcon>
        <path
          fill="currentColor"
          d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.657l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
        />
      </SocialIcon>
    ),
  },
  {
    key: 'reddit',
    labelKey: 'common.footer.followReddit',
    href: 'https://www.reddit.com/r/fetmode',
    icon: (
      <SocialIcon>
        <path
          fill="currentColor"
          d="M22 12.06c0-1.2-.98-2.17-2.18-2.17-.58 0-1.1.23-1.5.6-1.47-1-3.46-1.63-5.65-1.72l.96-4.53 3.15.67a1.56 1.56 0 1 0 .17-.98l-3.52-.75a.49.49 0 0 0-.58.38l-1.07 5.04c-2.24.07-4.27.7-5.77 1.72a2.17 2.17 0 1 0-2.4 3.55 4 4 0 0 0-.05.63c0 3.2 3.73 5.8 8.33 5.8s8.33-2.6 8.33-5.8c0-.2-.02-.4-.05-.6.7-.39 1.15-1.12 1.15-1.95zM7.5 13.5a1.25 1.25 0 1 1 2.5 0 1.25 1.25 0 0 1-2.5 0zm7.06 3.6c-.86.86-2.5.93-2.98.93-.48 0-2.12-.07-2.98-.93a.33.33 0 0 1 .47-.47c.54.54 1.7.73 2.51.73.81 0 1.97-.19 2.51-.73a.33.33 0 1 1 .47.47zM14.25 14.75a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5z"
        />
      </SocialIcon>
    ),
  },
  {
    key: 'instagram',
    labelKey: 'common.footer.followInstagram',
    href: 'https://www.instagram.com/fetmode',
    icon: (
      <SocialIcon>
        <path
          fill="currentColor"
          d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.7 3.7 0 0 1-1.38-.9 3.7 3.7 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16zm0 3.29a6.55 6.55 0 1 0 0 13.1 6.55 6.55 0 0 0 0-13.1zm0 10.8a4.25 4.25 0 1 1 0-8.5 4.25 4.25 0 0 1 0 8.5zm6.81-11.05a1.53 1.53 0 1 0 0 3.06 1.53 1.53 0 0 0 0-3.06z"
        />
      </SocialIcon>
    ),
  },
  {
    key: 'telegram',
    labelKey: 'common.footer.followTelegram',
    href: 'https://t.me/fetmode',
    icon: (
      <SocialIcon>
        <path
          fill="currentColor"
          d="M21.94 4.66a1.2 1.2 0 0 0-1.62-1.15L2.9 10.27c-1.06.41-1.05 1-.19 1.27l4.5 1.4 1.74 5.34c.22.62.4.85.78.85.36 0 .55-.17.79-.4l2.16-2.1 4.5 3.32c.83.46 1.42.22 1.63-.77l2.94-13.8zM9.6 13.98l8.68-5.47c.38-.23.74-.11.45.15l-7.4 6.68-.29 3.06z"
        />
      </SocialIcon>
    ),
  },
] as const;

export function StudioSiteFooter(): ReactElement {
  const { user } = useAuth();
  const columns = buildColumns(Boolean(user));
  const year = new Date().getFullYear();

  return (
    <footer className="studio-footer">
      <div className="studio-footer__inner">
        <div className="studio-footer__top">
          <div className="studio-footer__brand">
            <Link to="/" className="studio-footer__brand-link">
              <img src="/brand/logo.png" alt="" width={32} height={32} aria-hidden />
              <span className="studio-footer__brand-name">{t('common.studio.app.name')}</span>
            </Link>
            <p className="studio-footer__tagline">{t('common.app.tagline')}</p>
            <p className="studio-footer__mission">{t('common.studio.footer.mission')}</p>
            <nav className="studio-footer__social" aria-label={t('common.footer.socialTitle')}>
              {SOCIAL.map((social) => (
                <a
                  key={social.key}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="studio-footer__social-link"
                  aria-label={t(social.labelKey)}
                >
                  {social.icon}
                </a>
              ))}
            </nav>
          </div>

          <div className="studio-footer__columns">
            {columns.map((column) => (
              <nav key={column.title} className="studio-footer__column" aria-label={column.title}>
                <h2 className="studio-footer__column-title">{column.title}</h2>
                <ul className="studio-footer__column-list">
                  {column.links.map((link) => (
                    <li key={link.key}>
                      {link.to ? (
                        <Link to={link.to} className="studio-footer__link">
                          {link.label}
                        </Link>
                      ) : (
                        <a
                          href={link.href}
                          className="studio-footer__link"
                          {...(link.href?.startsWith('http')
                            ? { target: '_blank', rel: 'noopener noreferrer' }
                            : {})}
                        >
                          {link.label}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </nav>
            ))}
          </div>
        </div>

        <p className="studio-footer__age">{t('common.footer.ageStatement')}</p>

        <div className="studio-footer__bottom">
          <div className="studio-footer__badges" aria-label={t('common.compliance.badgesLabel')}>
            <a
              href="https://www.rtalabel.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="studio-footer__badge"
            >
              RTA
            </a>
            <a
              href="https://www.asacp.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="studio-footer__badge"
            >
              ASACP
            </a>
          </div>
          <span className="studio-footer__copyright">
            {t('common.footer.copyright').replace('{{year}}', String(year))}
          </span>
        </div>
      </div>
    </footer>
  );
}
